import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { getToken } from "./persist/authSideEffects";
import {
  AuditLog,
  AxDataTableRequestInput,
  AxDataTableResponse,
  AxDataTableDynamicRequestParams,
  Currency,
  LinkedLicence,
  LoginLog,
  MyFirmListItem,
  TraceLog,
  UserRight,
  ContentGroup,
  FirmApiToken,
  Content,
  ContentGroupMember,
  Role,
  RoleMenu,
  Menu,
  Entity,
  MenuEntity,
  RightEnum,
  Allergy,
  Store,
  StoreDetails,
  StoreSalePack,
  HospitalityMenu,
  HospitalityMenuDetails,
  HospitalityMenuFile,
  HospitalityMenuCategory,
  HospitalityMenuCategoryItem,
  HospitalityMenuCategoryItemAllergy,
  User,
  UserDetails,
  UserFirm,
  AppUserRole,
  Firm,
  FirmDetails,
  MenuDetails,
  TemplateResponse,
  ContentFolder,
  Tag,
  ScreenSize,
  ScreenSizeDetails,
  Player,
  Channel,
  ChannelDetails,
  PlayerDetails,
  ChannelDetailItem,
  ContentTag,
  Area,
  FirmDashboard,
  DayOfWeek,
  ChannelDetailDate,
  Campaign,
  CampaignDetails,
  CampaignDetailItem,
  CampaignDetailDate,
  PlayerCampaign,
  AreaCampaign,
  AreaDetails,
  ContentGroupTag,
  CampaignTag,
  PlayerTag,
  ChannelTag,
  AreaType,
  AreaTypeDetails,
  AreaContent,
  CampaignDetailItemContent,
  ChannelDetailItemContent,
  MenuHeader,
  MenuHeaderDetails,
  MenuHeaderMember,
  Api,
  MenuApi,
  StockTemplateResponse,
  BlogPost,
  BlogPostDetails,
  PlayerSyncStatus,
  PlayerHealth,
  PlayerStats,
  Notification,
  SearchResponseItem,
  SystemDynamicSetting,
  ContentGroupType,
  ContentGroupMemberCategory,
  RobotAge,
  RobotEmotion,
  RobotGender,
  CampaignType,
} from "../lib/types";
import { API_ENDPOINTS } from "@/constants/apiConstants";
import settings from "@/settings.json";
import { CanvasData } from "@/components/canvas/types/canvasTypes";
import { selectToken } from "@/store/appSelectors";
import store from "@/store/store";
import { DSPlayerOBj } from "@/components/ds/lib/types";

const api = axios.create({
  baseURL: settings.Server_Url,
});

const axiosConfig: AxiosRequestConfig = {
  headers: {
    "Content-Type": "application/x-www-form-urlencoded",
    Accept: "*/*",
  },
};

api.interceptors.request.use((config) => {
  const token = selectToken(store.getState()) || getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const codeRequest = (email: string): Promise<AxiosResponse<boolean | string>> => {
  const formData = new FormData();
  formData.append("Email", email);
  return api.post(API_ENDPOINTS.AUTH.CODE_REQUEST, formData, axiosConfig);
};

export const loginWithOtp = (
  email: string,
  password: string,
  otp: number
): Promise<AxiosResponse<{ auth_token: string }>> => {
  const formData = new FormData();
  formData.append("Email", email);
  formData.append("Password", password);
  formData.append("Code", `${otp}`);
  return api.post(API_ENDPOINTS.AUTH.CODE_LOGIN, formData, axiosConfig);
};

export const changePassword = (
  previousPassword: string,
  password: string,
  userId: string
): Promise<AxiosResponse<{ auth_token: string }>> => {
  const formData = new FormData();
  formData.append("PreviousPassword", previousPassword);
  formData.append("Password", password);
  formData.append("userId", userId);
  return api.post(API_ENDPOINTS.AUTH.UPDATE_PASSWORD, formData, axiosConfig);
};

export const forgotPassword = (email: string): Promise<AxiosResponse<boolean | string>> => {
  const formData = new FormData();
  formData.append("Email", email);
  return api.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, formData, axiosConfig);
};

export const getUserRight = (): Promise<AxiosResponse<UserRight>> => {
  return api.put(API_ENDPOINTS.USER.RIGHTS, {}, axiosConfig);
};

export const getMyFirmList = (): Promise<AxiosResponse<MyFirmListItem[]>> => {
  return api.put(API_ENDPOINTS.USER.FIRM_LIST, {}, axiosConfig);
};

const axTableInput = (input: AxDataTableRequestInput) => {
  const formData = new FormData();
  formData.append("numberOfObjectsPerPage", String(input.numberOfObjectsPerPage));
  formData.append("search", input.search || "");
  formData.append("pageNumber", String(input.pageNumber));
  if (input.references) {
    formData.append("references", JSON.stringify(input.references));
  }
  return formData;
};

export const getAuditLog = (input: AxDataTableRequestInput): Promise<AxiosResponse<AxDataTableResponse<AuditLog>>> => {
  return api.put(API_ENDPOINTS.LOG.AUDIT, axTableInput(input), axiosConfig);
};

export const getLoginLog = (input: AxDataTableRequestInput): Promise<AxiosResponse<AxDataTableResponse<LoginLog>>> => {
  return api.put(API_ENDPOINTS.LOG.LOGIN, axTableInput(input), axiosConfig);
};

export const getTraceLog = (input: AxDataTableRequestInput): Promise<AxiosResponse<AxDataTableResponse<TraceLog>>> => {
  return api.put(API_ENDPOINTS.LOG.TRACE, axTableInput(input), axiosConfig);
};

export const getCurrencies = (
  input: AxDataTableRequestInput
): Promise<AxiosResponse<AxDataTableResponse<Currency>>> => {
  return api.put(API_ENDPOINTS.CURRENCY, axTableInput(input), axiosConfig);
};

export const getLinkedLicences = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<LinkedLicence>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.Available !== "undefined") {
    formData.append("Available", dynamicRequestParams.Available.toString());
  }
  if (typeof dynamicRequestParams?.FirmGuid !== "undefined") {
    formData.append("FirmGuid", dynamicRequestParams.FirmGuid.toString());
  }
  return api.put(API_ENDPOINTS.FIRM_LICENCES.GET, formData, axiosConfig);
};

export const removeLinkedLicence = (firmGuid: string, firmLicenceGuid: string): Promise<AxiosResponse<boolean>> => {
  const formData = new FormData();
  formData.append("FirmLicenceGuid", firmLicenceGuid);
  formData.append("FirmGuid", firmGuid);
  return api.post(API_ENDPOINTS.FIRM_LICENCES.REMOVE, formData, axiosConfig);
};

export const getContent = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<Content>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.FirmGuid !== "undefined") {
    formData.append("FirmGuid", dynamicRequestParams.FirmGuid.toString());
  }
  if (typeof dynamicRequestParams?.isCustom !== "undefined") {
    formData.append("IsCustom", dynamicRequestParams.isCustom.toString());
  }
  if (typeof dynamicRequestParams?.FolderGuid !== "undefined") {
    formData.append("FolderGuid", dynamicRequestParams.FolderGuid.toString());
  }
  if (typeof dynamicRequestParams?.ScreenSizeGuid !== "undefined" && dynamicRequestParams.ScreenSizeGuid) {
    formData.append("ScreenSizeGuid", dynamicRequestParams.ScreenSizeGuid.toString());
  }
  if (typeof dynamicRequestParams?.TagGuid !== "undefined" && dynamicRequestParams.TagGuid) {
    formData.append("TagGuid", dynamicRequestParams.TagGuid.toString());
  }
  return api.put(API_ENDPOINTS.CONTENT.GET, formData, axiosConfig);
};

export const getContentDetails = (firmGuid: string, contentGuid: string): Promise<AxiosResponse<Content>> => {
  const formData = new FormData();
  formData.append("FirmGuid", firmGuid);
  formData.append("ContentGuid", contentGuid);

  const url = `${API_ENDPOINTS.CONTENT.GET_DETAILS}/${contentGuid}`;

  return api.put(url, formData, axiosConfig);
};

export const updateContentFolder = (
  firmGuid: string,
  contentGuid: string,
  folderGuid: string
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("FirmGuid", firmGuid);
  formData.append("ContentGuid", contentGuid);
  formData.append("FolderGuid", folderGuid);

  const url = `${API_ENDPOINTS.CONTENT.UPDATE_FOLDER}/${contentGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const addFolder = (
  firmGuid: string,
  folderName: string,
  color: string,
  mainFolderId: string
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("FirmGuid", firmGuid);
  formData.append("Name", folderName);
  if (mainFolderId) formData.append("MainFolderId", mainFolderId);
  formData.append("Order", "0");
  formData.append("Color", color);
  return api.post(API_ENDPOINTS.FOLDER.ADD, formData, axiosConfig);
};

export const updateFolderParent = (
  firmGuid: string,
  folderGuid: string,
  parentFolderGuid: string
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("FirmGuid", firmGuid);
  formData.append("Id", folderGuid);
  formData.append("MainFolderId", parentFolderGuid);

  const url = `${API_ENDPOINTS.FOLDER.UPDATE_MAIN_FOLDER}/${folderGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const getContentFolder = (firmGuid: string): Promise<AxiosResponse<AxDataTableResponse<ContentFolder>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 1000,
    pageNumber: 1,
  });

  formData.append("FirmGuid", firmGuid);

  return api.put(API_ENDPOINTS.FOLDER.GET, formData, axiosConfig);
};

export const renameContentFolder = (
  firmGuid: string,
  folderGuid: string,
  name: string,
  color: string
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("FirmGuid", firmGuid);
  formData.append("Id", folderGuid);
  formData.append("Name", name);
  formData.append("Color", color);

  const url = `${API_ENDPOINTS.FOLDER.UPDATE}/${folderGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteContentFolder = (firmGuid: string, folderGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("FirmGuid", firmGuid);
  formData.append("Id", folderGuid);

  const url = `${API_ENDPOINTS.FOLDER.DELETE}/${folderGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteContent = (firmGuid: string, contentGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("FirmGuid", firmGuid);
  formData.append("ContentGuid", contentGuid);

  return api.post(API_ENDPOINTS.CONTENT.DELETE, formData, axiosConfig);
};

export const uploadContents = (values: {
  firmGuid: string;
  isCustom: boolean;
  files: File[];
  templateId?: string;
  startDate?: string;
  endDate?: string;
  folderGuid?: string;
  name: string;
  screenSizeId: number;
  duration?: number;
}): Promise<AxiosResponse<{ value: Content[] }>> => {
  const formData = new FormData();
  formData.append("FirmGuid", values.firmGuid);
  values.files.forEach((file) => {
    const newFile = new File([file], values.name, { type: file.type });
    formData.append("Files", newFile);
  });
  formData.append("IsCustom", values.isCustom.toString());
  if (values.templateId) {
    formData.append("DsTemplateGuid", values.templateId);
  }
  if (values.startDate) {
    formData.append("StartDate", values.startDate);
  }
  if (values.endDate) {
    formData.append("EndDate", values.endDate);
  }
  if (values.folderGuid) {
    formData.append("FolderGuid", values.folderGuid);
  }
  if (values.screenSizeId) {
    formData.append("ScreenSize_Id", values.screenSizeId.toString());
  }
  if (values.duration) {
    formData.append("Duration", values.duration.toString());
  }

  return api.post(API_ENDPOINTS.CONTENT.UPLOAD, formData, axiosConfig);
};

export const uploadWithoutContent = (values: {
  firmGuid: string;
  files: File[];
  name: string;
}): Promise<AxiosResponse<{ value: { contentStorageName: string } }>> => {
  const formData = new FormData();
  formData.append("FirmGuid", values.firmGuid);
  values.files.forEach((file) => {
    const newFile = new File([file], values.name, { type: file.type });
    formData.append("Files", newFile);
  });

  return api.post(API_ENDPOINTS.CONTENT.UPLOAD_WITHOUT_CONTENT, formData, axiosConfig);
};

export const updateContent = async (values: {
  firmGuid: string;
  contentGuid: string;
  files: any[];
  name: string;
  templateLayoutConfig: string;
}): Promise<AxiosResponse<{ value: Content[] }>> => {
  const formData = new FormData();
  formData.append("FirmGuid", values.firmGuid);
  formData.append("ContentGuid", values.contentGuid);
  formData.append("TemplateLayoutConfig", values.templateLayoutConfig);

  values.files.forEach((file) => {
    const newFile = new File([file], values.name, { type: file.type });
    formData.append("Files", newFile);
  });

  const url = `${API_ENDPOINTS.CONTENT.UPDATE_CONTENT_FILE}/${values.contentGuid}?FirmGuid=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const addContent = (values: {
  firmGuid: string;
  folderGuid?: string;
  isCustom: boolean;
  contentType: string;
  name: string;
  contentLink: string;
  startDate?: string;
  endDate?: string;
  duration?: number;
  screenSizeId: number;
}): Promise<AxiosResponse<{ value: Content[] }>> => {
  const formData = new FormData();
  formData.append("FirmGuid", values.firmGuid);
  if (values.folderGuid) formData.append("FolderGuid", values.folderGuid);
  formData.append("IsCustom", values.isCustom.toString());
  formData.append("ContentType", values.contentType);
  formData.append("Name", values.name);
  formData.append("ContentLink", values.contentLink);
  if (values.startDate) formData.append("StartDate", values.startDate);
  if (values.endDate) formData.append("EndDate", values.endDate);
  if (values.duration) formData.append("Duration", values.duration.toString());
  if (values.screenSizeId) {
    formData.append("ScreenSize_Id", values.screenSizeId.toString());
  }
  return api.post(API_ENDPOINTS.CONTENT.ADD_CONTENT, formData, axiosConfig);
};

export const addContentSplit = (values: {
  firmGuid: string;
  folderGuid?: string;
  name: string;
  startDate?: string;
  endDate?: string;
  screenSizeId: number;
  layoutConfig: string;
}): Promise<AxiosResponse<Content[]>> => {
  const formData = new FormData();
  formData.append("FirmGuid", values.firmGuid);
  if (values.folderGuid) formData.append("FolderGuid", values.folderGuid);
  formData.append("ContentType", "split");
  formData.append("Name", values.name);
  if (values.startDate) formData.append("StartDate", values.startDate);
  if (values.endDate) formData.append("EndDate", values.endDate);
  formData.append("ScreenSize_Id", values.screenSizeId.toString());
  formData.append("layout_config", values.layoutConfig);

  return api.post(API_ENDPOINTS.CONTENT.ADD_CONTENT, formData, axiosConfig);
};

export const updateContentDates = (values: {
  firmGuid: string;
  contentGuid: string;
  startDate?: string;
  endDate?: string;
}): Promise<AxiosResponse<{ value: Content[] }>> => {
  const formData = new FormData();
  formData.append("FirmGuid", values.firmGuid);
  formData.append("ContentGuid", values.contentGuid);
  formData.append("StartDate", values.startDate || "");
  formData.append("EndDate", values.endDate || "");

  const url = `${API_ENDPOINTS.CONTENT.UPDATE_CONTENT_DATES}/${values.contentGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const updateContentLink = (values: {
  firmGuid: string;
  contentGuid: string;
  link: string;
}): Promise<AxiosResponse<{ value: Content[] }>> => {
  const formData = new FormData();
  formData.append("FirmGuid", values.firmGuid);
  formData.append("ContentGuid", values.contentGuid);
  formData.append("ContentLink", values.link);

  const url = `${API_ENDPOINTS.CONTENT.UPDATE_CONTENT_LINK}/${values.contentGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const updateContentSizes = (values: {
  firmGuid: string;
  contentGuid: string;
  layoutConfig: string;
  screenSizeId: number;
}): Promise<AxiosResponse<{ value: Content[] }>> => {
  const formData = new FormData();
  formData.append("FirmGuid", values.firmGuid);
  formData.append("ContentGuid", values.contentGuid);
  formData.append("ScreenSize_Id", values.screenSizeId.toString());
  formData.append("layout_config", values.layoutConfig);

  const url = `${API_ENDPOINTS.CONTENT.CONTENT_UPDATE_SIZES}/${values.contentGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const renameContent = (values: {
  firmGuid: string;
  contentGuid: string;
  name: string;
}): Promise<AxiosResponse<{ value: Content[] }>> => {
  const formData = new FormData();
  formData.append("FirmGuid", values.firmGuid);
  formData.append("ContentGuid", values.contentGuid);
  formData.append("Name", values.name);

  const url = `${API_ENDPOINTS.CONTENT.CONTENT_RENAME}/${values.contentGuid}?FirmGuid=${values.firmGuid}&Name=${values.name}`;

  return api.post(url, formData, axiosConfig);
};

export const getContentGroups = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<ContentGroup>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.FirmGuid !== "undefined") {
    formData.append("FirmGuid", dynamicRequestParams.FirmGuid.toString());
  }
  if (typeof dynamicRequestParams?.Type !== "undefined") {
    formData.append("Type", dynamicRequestParams.Type.toString());
  }
  if (typeof dynamicRequestParams?.ScreenSizeGuid !== "undefined" && dynamicRequestParams.ScreenSizeGuid) {
    formData.append("ScreenSizeGuid", dynamicRequestParams.ScreenSizeGuid.toString());
  }
  if (typeof dynamicRequestParams?.TagGuid !== "undefined" && dynamicRequestParams.TagGuid) {
    formData.append("TagGuid", dynamicRequestParams.TagGuid.toString());
  }
  return api.put(API_ENDPOINTS.CONTENT_GROUP.GET, formData, axiosConfig);
};

export const addContentGroup = (values: {
  firmGuid: string;
  name: string;
  screenSizeId: number;
  type: ContentGroupType;
}): Promise<AxiosResponse<{ guid: string }>> => {
  const formData = new FormData();
  formData.append("firmGuid", values.firmGuid);
  formData.append("name", values.name);
  formData.append("ScreenSize_Id", values.screenSizeId.toString());
  formData.append("type", values.type.toString());
  return api.post(API_ENDPOINTS.CONTENT_GROUP.ADD, formData, axiosConfig);
};

export const updateContentGroup = (values: {
  firmGuid: string;
  contentGroupGuid: string;
  name: string;
  isShuffle: boolean;
  screenSizeId: number;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("firmGuid", values.firmGuid);
  formData.append("ContentGroupGuid", values.contentGroupGuid);
  formData.append("name", values.name);
  formData.append("isShuffle", values.isShuffle.toString());
  formData.append("ScreenSize_Id", values.screenSizeId.toString());

  const url = `${API_ENDPOINTS.CONTENT_GROUP.UPDATE}/${values.contentGroupGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const publishEntity = (
  firmGuid: string,
  uuid: string,
  entity: "PlayerGuid" | "ContentGroupGuid" | "ContentGuid" | "ChannelGuid" | "CampaignGuid"
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("FirmGuid", firmGuid);
  formData.append(entity, uuid);

  const url = `${API_ENDPOINTS.PUBLISH}?FirmGuid=${firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteContentGroup = (firmGuid: string, contentGroupGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("firmGuid", firmGuid);
  formData.append("ContentGroupGuid", contentGroupGuid);

  const url = `${API_ENDPOINTS.CONTENT_GROUP.DELETE}/${contentGroupGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const getContentGroupDetails = (
  firmGuid: string,
  contentGroupGuid: string
): Promise<AxiosResponse<ContentGroup>> => {
  const formData = new FormData();
  formData.append("firmGuid", firmGuid);
  formData.append("ContentGroupGuid", contentGroupGuid);
  return api.put(API_ENDPOINTS.CONTENT_GROUP.GET_DETAILS, formData, axiosConfig);
};

export const getContentGroupMembers = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<ContentGroupMember>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.FirmGuid !== "undefined") {
    formData.append("FirmGuid", dynamicRequestParams.FirmGuid.toString());
  }
  if (typeof dynamicRequestParams?.ContentGroupGuid !== "undefined") {
    formData.append("ContentGroupGuid", dynamicRequestParams.ContentGroupGuid.toString());
  }
  return api.put(API_ENDPOINTS.CONTENT_GROUP_MEMBER.GET, formData, axiosConfig);
};

export const addContentGroupMembers = (
  firmGuid: string,
  contentGroupGuid: string,
  contentGuid: string,
  duration?: number
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("FirmGuid", firmGuid);
  formData.append("ContentGroupGuid", contentGroupGuid);
  formData.append("ContentGuid", contentGuid);
  formData.append("Duration", (duration || 0).toString());
  formData.append("isActive", "true");
  formData.append("Order", "0");
  return api.post(API_ENDPOINTS.CONTENT_GROUP_MEMBER.ADD, formData, axiosConfig);
};

export const deleteContentGroupMember = (
  firmGuid: string,
  contentGroupMemberGuid: string
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("FirmGuid", firmGuid);
  formData.append("ContentGroupMemberGuid", contentGroupMemberGuid);

  const url = `${API_ENDPOINTS.CONTENT_GROUP_MEMBER.DELETE}/${contentGroupMemberGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const updateContentGroupMembers = (
  firmGuid: string,
  contentGroupMemberGuid: string,
  params: {
    duration: string;
    order: string;
    isActive: string;
    emotion: RobotEmotion;
    age: RobotAge;
    gender: RobotGender;
    categoryId: string;
  }
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("FirmGuid", firmGuid);
  formData.append("ContentGroupMemberGuid", contentGroupMemberGuid);
  formData.append("Duration", params.duration);
  formData.append("Order", params.order);
  formData.append("isActive", params.isActive);
  formData.append("CameraEmotion", params.emotion.toString());
  formData.append("CameraAge", params.age.toString());
  formData.append("Gender", params.gender.toString());
  if (params.categoryId) {
    formData.append("ContentGroupMemberCategoryGuid", params.categoryId);
  }

  const url = `${API_ENDPOINTS.CONTENT_GROUP_MEMBER.UPDATE}/${contentGroupMemberGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const assignWebLinkLicence = (
  firmGuid: string,
  firmLicenceGuid: string,
  contentGroupGuid: string
): Promise<AxiosResponse<boolean>> => {
  const formData = new FormData();
  formData.append("FirmLicenceGuid", firmLicenceGuid);
  formData.append("FirmGuid", firmGuid);
  formData.append("ContentGroupGuid", contentGroupGuid);
  return api.post(API_ENDPOINTS.FIRM_LICENCES.ASSIGN_WEBLINK, formData, axiosConfig);
};

export const removeAssignedGroupContent = (
  firmGuid: string,
  firmLicenceGuid: string
): Promise<AxiosResponse<boolean>> => {
  const formData = new FormData();
  formData.append("FirmLicenceGuid", firmLicenceGuid);
  formData.append("FirmGuid", firmGuid);
  return api.post(API_ENDPOINTS.FIRM_LICENCES.REMOVE_ASSIGNED_GROUP_CONTENT, formData, axiosConfig);
};

export const getFirmApiToken = (firmGuid: string): Promise<AxiosResponse<FirmApiToken>> => {
  const formData = new FormData();
  formData.append("FirmGuid", firmGuid);
  return api.put(API_ENDPOINTS.FIRM_API_TOKEN.GET, formData, axiosConfig);
};

export const registerFirmAPI = (firmGuid: string): Promise<AxiosResponse<FirmApiToken>> => {
  const formData = new FormData();
  formData.append("FirmGuid", firmGuid);
  return api.post(API_ENDPOINTS.FIRM_API_TOKEN.REGISTER, formData, axiosConfig);
};

export const getRoles = (input: AxDataTableRequestInput): Promise<AxiosResponse<AxDataTableResponse<Role>>> => {
  const formData = axTableInput(input);
  return api.put(API_ENDPOINTS.ROLE.GET, formData, axiosConfig);
};

export const getRoleDetails = (roleId: number): Promise<AxiosResponse<Role>> => {
  const formData = new FormData();
  formData.append("Id", roleId.toString());
  return api.put(API_ENDPOINTS.ROLE.GET_DETAILS, formData, axiosConfig);
};

export const addRole = (roleName: string): Promise<AxiosResponse<{ id: number }>> => {
  const formData = new FormData();
  formData.append("RoleName", roleName);
  formData.append("ID", "0");

  return api.post(API_ENDPOINTS.ROLE.ADD, formData, axiosConfig);
};

export const updateRole = (roleId: number, roleName: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("RoleName", roleName);
  formData.append("ID", roleId.toString());

  const url = `${API_ENDPOINTS.ROLE.UPDATE}/${roleId}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteRole = (roleId: number): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", roleId.toString());

  const url = `${API_ENDPOINTS.ROLE.DELETE}/${roleId}`;

  return api.post(url, formData, axiosConfig);
};

export const getMenu = (input: AxDataTableRequestInput): Promise<AxiosResponse<AxDataTableResponse<Menu>>> => {
  const formData = axTableInput(input);
  return api.put(API_ENDPOINTS.MENU.GET, formData, axiosConfig);
};

export const getMenuDetails = (menuId: number): Promise<AxiosResponse<MenuDetails>> => {
  const formData = new FormData();
  formData.append("Id", menuId.toString());
  return api.put(API_ENDPOINTS.MENU.GET_DETAILS, formData, axiosConfig);
};

export const deleteMenu = (menuId: number): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", menuId.toString());

  const url = `${API_ENDPOINTS.MENU.DELETE}/${menuId}`;

  return api.post(url, formData, axiosConfig);
};

export const addMenu = (values: {
  menuName: string;
  description: string;
  order: string;
  menuIcon: string;
  path: string;
  adminOnly: boolean;
}): Promise<AxiosResponse<{ id: number }>> => {
  const formData = new FormData();
  formData.append("MenuName", values.menuName);
  formData.append("Description", values.description);
  formData.append("Order", values.order);
  formData.append("MenuIcon", values.menuIcon);
  formData.append("Path", values.path);
  formData.append("AdminOnly", values.adminOnly.toString());

  return api.post(API_ENDPOINTS.MENU.ADD, formData, axiosConfig);
};

export const updateMenu = (values: {
  id: number;
  menuName: string;
  description: string;
  order: string;
  menuIcon: string;
  path: string;
  adminOnly: boolean;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", values.id.toString());
  formData.append("MenuName", values.menuName);
  formData.append("Description", values.description);
  formData.append("Order", values.order);
  formData.append("MenuIcon", values.menuIcon);
  formData.append("Path", values.path);
  formData.append("AdminOnly", values.adminOnly.toString());

  const url = `${API_ENDPOINTS.MENU.UPDATE}/${values.id}`;

  return api.post(url, formData, axiosConfig);
};

export const getMenuHeader = (
  input: AxDataTableRequestInput
): Promise<AxiosResponse<AxDataTableResponse<MenuHeader>>> => {
  const formData = axTableInput(input);
  return api.put(API_ENDPOINTS.MENU_HEADER.GET, formData, axiosConfig);
};

export const getMenuHeaderDetails = (menuHeaderId: number): Promise<AxiosResponse<MenuHeaderDetails>> => {
  const formData = new FormData();
  formData.append("Id", menuHeaderId.toString());
  return api.put(API_ENDPOINTS.MENU_HEADER.GET_DETAILS, formData, axiosConfig);
};

export const addMenuHeader = (menuHeaderName: string): Promise<AxiosResponse<{ id: number }>> => {
  const formData = new FormData();
  formData.append("Name", menuHeaderName);
  return api.post(API_ENDPOINTS.MENU_HEADER.ADD, formData, axiosConfig);
};

export const updateMenuHeader = (
  menuHeaderId: number,
  menuHeaderName: string,
  order: number
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", menuHeaderId.toString());
  formData.append("Name", menuHeaderName);
  formData.append("Order", order.toString());
  const url = `${API_ENDPOINTS.MENU_HEADER.UPDATE}/${menuHeaderId}`;
  return api.post(url, formData, axiosConfig);
};

export const deleteMenuHeader = (menuHeaderId: number): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", menuHeaderId.toString());
  const url = `${API_ENDPOINTS.MENU_HEADER.DELETE}/${menuHeaderId}`;
  return api.post(url, formData, axiosConfig);
};

export const getMenuHeaderMembers = (
  menuHeaderId: number
): Promise<AxiosResponse<AxDataTableResponse<MenuHeaderMember>>> => {
  const formData = new FormData();
  formData.append("MenuId", menuHeaderId.toString());
  return api.put(API_ENDPOINTS.MENU_HEADER_MEMBER.GET, formData, axiosConfig);
};

export const addMenuHeaderMember = (menuHeaderId: number, menuId: number): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("MenuHeader_Id", menuHeaderId.toString());
  formData.append("Menu_Id", menuId.toString());
  return api.post(API_ENDPOINTS.MENU_HEADER_MEMBER.ADD, formData, axiosConfig);
};

export const deleteMenuHeaderMember = (menuHeaderMemberId: number): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", menuHeaderMemberId.toString());
  const url = `${API_ENDPOINTS.MENU_HEADER_MEMBER.DELETE}/${menuHeaderMemberId}`;
  return api.post(url, formData, axiosConfig);
};

export const getRoleMenu = (roleId: number): Promise<AxiosResponse<AxDataTableResponse<RoleMenu>>> => {
  const formData = new FormData();
  formData.append("Role_Id", roleId.toString());

  return api.put(API_ENDPOINTS.ROLE_MENU.GET, formData, axiosConfig);
};

export const getEntity = (): Promise<AxiosResponse<AxDataTableResponse<Entity>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 1000,
    pageNumber: 1,
  });
  return api.put(API_ENDPOINTS.ENTITY.GET, formData, axiosConfig);
};

export const getMenuEntity = (menuId: number): Promise<AxiosResponse<AxDataTableResponse<MenuEntity>>> => {
  const formData = new FormData();
  formData.append("Menu_Id", menuId.toString());

  return api.put(API_ENDPOINTS.MENU_ENTITY.GET, formData, axiosConfig);
};

export const deleteMenuEntity = (menuEntityId: number): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", menuEntityId.toString());

  const url = `${API_ENDPOINTS.MENU_ENTITY.DELETE}/${menuEntityId}`;

  return api.post(url, formData, axiosConfig);
};

export const addMenuEntity = (entityId: number, menuId: number, right: number): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Entity_Id", entityId.toString());
  formData.append("Menu_Id", menuId.toString());
  formData.append("Right", right.toString());

  const url = `${API_ENDPOINTS.MENU_ENTITY.ADD}`;

  return api.post(url, formData, axiosConfig);
};

export const updateMenuEntity = (
  id: number,
  entityId: number,
  menuId: number,
  right: number
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("id", id.toString());
  formData.append("Entity_Id", entityId.toString());
  formData.append("Menu_Id", menuId.toString());
  formData.append("Right", right.toString());

  const url = `${API_ENDPOINTS.MENU_ENTITY.UPDATE}/${id}`;

  return api.post(url, formData, axiosConfig);
};

export const getRightEnum = (): Promise<AxiosResponse<RightEnum[]>> => {
  return api.put(API_ENDPOINTS.ENUM.RIGHT.GET, {}, axiosConfig);
};

export const deleteRoleMenu = (roleMenuId: number): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", roleMenuId.toString());

  const url = `${API_ENDPOINTS.ROLE_MENU.DELETE}/${roleMenuId}`;

  return api.post(url, formData, axiosConfig);
};

export const addRoleMenu = (roleId: number, menuId: number): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Role_Id", roleId.toString());
  formData.append("Menu_Id", menuId.toString());

  return api.post(API_ENDPOINTS.ROLE_MENU.ADD, formData, axiosConfig);
};

export const getAllergy = (input: AxDataTableRequestInput): Promise<AxiosResponse<AxDataTableResponse<Allergy>>> => {
  const formData = axTableInput(input);

  return api.put(API_ENDPOINTS.ALLERGY.GET, formData, axiosConfig);
};

export const getAllergyDetails = (id: number): Promise<AxiosResponse<Allergy>> => {
  const formData = new FormData();
  formData.append("Id", id.toString());

  return api.put(API_ENDPOINTS.ALLERGY.GET_DETAILS, formData, axiosConfig);
};

export const addAllergy = (name: string): Promise<AxiosResponse<{ id: number }>> => {
  const formData = new FormData();
  formData.append("Name", name);

  return api.post(API_ENDPOINTS.ALLERGY.ADD, formData, axiosConfig);
};

export const deleteAllergy = (allergyId: number): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", allergyId.toString());

  const url = `${API_ENDPOINTS.ALLERGY.DELETE}/${allergyId}`;

  return api.post(url, formData, axiosConfig);
};

export const updateAllergy = (allergyId: number, name: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", allergyId.toString());
  formData.append("Name", name);

  const url = `${API_ENDPOINTS.ALLERGY.UPDATE}/${allergyId}`;

  return api.post(url, formData, axiosConfig);
};

export const getStore = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<Store>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.FirmId !== "undefined") {
    formData.append("references", JSON.stringify({ Firm_Id: dynamicRequestParams.FirmId }));
  }
  return api.put(API_ENDPOINTS.STORE.GET, formData, axiosConfig);
};

export const getStoreDetails = (storeId: string): Promise<AxiosResponse<StoreDetails>> => {
  const formData = new FormData();
  formData.append("Id", storeId.toString());

  return api.put(API_ENDPOINTS.STORE.GET_DETAILS, formData, axiosConfig);
};

export const addStore = (values: {
  firmGuid: string;
  name: string;
  currencyId?: number;
  customerKey?: string;
}): Promise<AxiosResponse<{ guid: string }>> => {
  const formData = new FormData();
  formData.append("firmGuid", values.firmGuid);
  formData.append("Name", values.name);
  formData.append("Currency_Id", String(values.currencyId));
  formData.append("CustomerKey", String(values.customerKey));

  return api.post(API_ENDPOINTS.STORE.ADD, formData, axiosConfig);
};

export const deleteStore = (storeId: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", storeId);

  return api.post(API_ENDPOINTS.STORE.DELETE, formData, axiosConfig);
};

export const updateStore = (values: {
  id: string;
  firmGuid: string;
  name: string;
  currencyId?: number;
  customerKey?: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", values.id);
  formData.append("firmGuid", values.firmGuid);
  formData.append("Name", values.name);
  formData.append("Currency_Id", String(values.currencyId));
  formData.append("CustomerKey", String(values.customerKey));

  const url = `${API_ENDPOINTS.STORE.UPDATE}/${values.id}`;

  return api.post(url, formData, axiosConfig);
};

export const getStoreSalePack = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<StoreSalePack>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.storeId !== "undefined") {
    formData.append("references", JSON.stringify({ Store_Id: dynamicRequestParams.storeId }));
  }
  return api.put(API_ENDPOINTS.STORE_SALE_PACK.GET, formData, axiosConfig);
};

export const deleteStoreSalePack = (storeSalePackId: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", storeSalePackId);

  return api.post(API_ENDPOINTS.STORE_SALE_PACK.DELETE, formData, axiosConfig);
};

export const addStoreSalePack = (values: {
  storeId: string;
  name: string;
  customerKey?: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Store_Id", values.storeId);
  formData.append("Name", values.name);
  formData.append("CustomerKey", String(values.customerKey));

  return api.post(API_ENDPOINTS.STORE_SALE_PACK.ADD, formData, axiosConfig);
};

export const getHospitalityMenu = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<HospitalityMenu>>> => {
  const formData = axTableInput(input);

  if (typeof dynamicRequestParams?.firmId !== "undefined") {
    formData.append("references", JSON.stringify({ Firm_Id: dynamicRequestParams.firmId, DataStatus: "1" }));
  }

  return api.put(API_ENDPOINTS.HOSPITALITY_MENU.GET, formData, axiosConfig);
};

export const getHospitalityMenuDetails = (id: number): Promise<AxiosResponse<HospitalityMenuDetails>> => {
  const formData = new FormData();
  formData.append("Id", id.toString());

  return api.put(API_ENDPOINTS.HOSPITALITY_MENU.GET_DETAILS, formData, axiosConfig);
};

export const addHospitalityMenu = (values: {
  firmGuid: string;
  name: string;
  pushId?: string;
  currencyId?: number;
  autoSyncOn?: boolean;
  autoSyncPeriodMinute?: string;
}): Promise<AxiosResponse<{ id: number }>> => {
  const formData = new FormData();
  formData.append("FirmGuid", values.firmGuid);
  formData.append("Name", values.name);
  formData.append("PushId", String(values.pushId));
  formData.append("Currency_Id", String(values.currencyId));
  formData.append("AutoSyncOn", String(values.autoSyncOn));
  formData.append("AutoSyncPeriodMinute", String(values.autoSyncPeriodMinute));

  return api.post(API_ENDPOINTS.HOSPITALITY_MENU.ADD, formData, axiosConfig);
};

export const updateHospitalityMenu = (values: {
  id: number;
  firmGuid: string;
  name: string;
  pushId?: string;
  currencyId?: number;
  autoSyncOn?: boolean;
  autoSyncPeriodMinute?: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", values.id.toString());
  formData.append("FirmGuid", values.firmGuid);
  formData.append("Name", values.name);
  formData.append("PushId", String(values.pushId));
  formData.append("Currency_Id", String(values.currencyId));
  formData.append("AutoSyncOn", String(values.autoSyncOn));
  formData.append("AutoSyncPeriodMinute", String(values.autoSyncPeriodMinute));

  const url = `${API_ENDPOINTS.HOSPITALITY_MENU.UPDATE}/${values.id}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteHospitalityMenu = (id: number): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id.toString());

  return api.post(API_ENDPOINTS.HOSPITALITY_MENU.DELETE, formData, axiosConfig);
};

export const syncHospitalityMenu = (id: number): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id.toString());

  const url = `${API_ENDPOINTS.HOSPITALITY_MENU.SYNC}/${id}`;

  return api.post(url, formData, axiosConfig);
};

export const getHospitalityMenuFile = (
  hospitalityMenuId: number,
  input: AxDataTableRequestInput
): Promise<AxiosResponse<AxDataTableResponse<HospitalityMenuFile>>> => {
  const formData = axTableInput(input);
  formData.append("references", JSON.stringify({ HMenu_Id: hospitalityMenuId.toString(), DataStatus: "1" }));

  return api.put(API_ENDPOINTS.HOSPITALITY_MENU_FILE.GET, formData, axiosConfig);
};

export const uploadHospitalityMenuFile = (hospitalityMenuId: number, file: File): Promise<AxiosResponse<Blob>> => {
  const formData = new FormData();
  formData.append("HMenu_Id", hospitalityMenuId.toString());
  formData.append("", file);

  return api.post(`${API_ENDPOINTS.HOSPITALITY_MENU_FILE.UPLOAD}/`, formData, axiosConfig);
};

export const getHospitalityMenuCategory = (id: number): Promise<AxiosResponse<HospitalityMenuCategory[]>> => {
  const formData = new FormData();
  formData.append("Id", id.toString());

  return api.put(API_ENDPOINTS.HOSPITALITY_MENU_CATEGORY.GET, formData, axiosConfig);
};

export const updateHospitalityMenuCategoryName = (
  hospitalityMenuId: number,
  categoryId: number,
  name: string
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", hospitalityMenuId.toString());
  formData.append("CategoryId", categoryId.toString());
  formData.append("CategoryName", name);

  const url = `${API_ENDPOINTS.HOSPITALITY_MENU_CATEGORY.UPDATE_NAME}/${categoryId}`;

  return api.post(url, formData, axiosConfig);
};

export const updateHospitalityMenuCategoryOrders = async (
  values: {
    hospitalityMenuId: number;
    categoryId: number;
    order: number;
  }[]
) => {
  for (const value of values) {
    const formData = new FormData();
    formData.append("Id", value.hospitalityMenuId.toString());
    formData.append("CategoryId", value.categoryId.toString());
    formData.append("Order", value.order.toString());

    const url = `${API_ENDPOINTS.HOSPITALITY_MENU_CATEGORY.UPDATE_ORDER}/${value.categoryId}`;

    await api.post(url, formData, axiosConfig);
  }
};
export const removeHospitalityMenuCategory = async (hospitalityMenuId: number, categoryId: number) => {
  const formData = new FormData();
  formData.append("Id", hospitalityMenuId.toString());
  formData.append("CategoryId", categoryId.toString());

  const url = `${API_ENDPOINTS.HOSPITALITY_MENU.REMOVE_CATEGORY}/${hospitalityMenuId}`;

  return await api.post(url, formData, axiosConfig);
};

export const addHospitalityMenuCategory = (
  hospitalityMenuId: number,
  name: string
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", hospitalityMenuId.toString());
  formData.append("CategoryName", name);

  const url = `${API_ENDPOINTS.HOSPITALITY_MENU_CATEGORY.ADD}/${hospitalityMenuId}`;

  return api.post(url, formData, axiosConfig);
};

export const addNewHospitalMenuCategoryItem = (values: {
  name: string;
  description: string;
  price: string;
  hospitalityMenuId: number;
  hospitalityMenuCategoryId: number;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Name", values.name);
  formData.append("description", values.description);
  formData.append("price", values.price);
  formData.append("HMenu_Id", values.hospitalityMenuId.toString());
  formData.append("HMenuCategory_Id", values.hospitalityMenuCategoryId.toString());

  return api.post(API_ENDPOINTS.HOSPITALITY_MENU_CATEGORY_ITEM.ADD, formData, axiosConfig);
};

export const getHospitalityMenuCategoryItem = (
  hospitalityMenuId: number
): Promise<AxiosResponse<AxDataTableResponse<HospitalityMenuCategoryItem>>> => {
  const formData = new FormData();
  formData.append("HMenuId", hospitalityMenuId.toString());
  formData.append("HMenuCategory_Id", "200");

  return api.put(API_ENDPOINTS.HOSPITALITY_MENU_CATEGORY_ITEM.GET, formData, axiosConfig);
};

export const deleteHospitalityMenuCategoryItem = async (
  hospitalityMenuId: number,
  hospitalityMenuCategoryItemId: number
) => {
  const formData = new FormData();
  formData.append("HMenu_Id", hospitalityMenuId.toString());
  formData.append("Id", hospitalityMenuCategoryItemId.toString());

  const url = `${API_ENDPOINTS.HOSPITALITY_MENU_CATEGORY_ITEM.DELETE}/${hospitalityMenuId}`;

  return await api.post(url, formData, axiosConfig);
};

export const updateHospitalMenuCategoryItem = (values: {
  id: number;
  name: string;
  description: string;
  price: string;
  hospitalityMenuId: number;
  hospitalityMenuCategoryId: number;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", values.id.toString());
  formData.append("Name", values.name);
  formData.append("description", values.description);
  formData.append("price", values.price);
  formData.append("HMenu_Id", values.hospitalityMenuId.toString());
  formData.append("HMenuCategory_Id", values.hospitalityMenuCategoryId.toString());

  const url = `${API_ENDPOINTS.HOSPITALITY_MENU_CATEGORY_ITEM.UPDATE}/${values.id}`;

  return api.post(url, formData, axiosConfig);
};

export const getHospitalityMenuCategoryItemFile = (
  hospitalityMenuCategoryItemId: number,
  input: AxDataTableRequestInput
): Promise<AxiosResponse<AxDataTableResponse<HospitalityMenuFile>>> => {
  const formData = axTableInput(input);
  formData.append(
    "references",
    JSON.stringify({
      HMenuItem_Id: hospitalityMenuCategoryItemId.toString(),
      DataStatus: "1",
    })
  );

  return api.put(API_ENDPOINTS.HOSPITALITY_MENU_CATEGORY_ITEM_FILE.GET, formData, axiosConfig);
};

export const uploadHospitalityMenuCategoryItemFile = (
  hospitalityMenuCategoryItemId: number,
  file: File
): Promise<AxiosResponse<Blob>> => {
  const formData = new FormData();
  formData.append("HMenuItem_Id", hospitalityMenuCategoryItemId.toString());
  formData.append("", file);

  return api.post(`${API_ENDPOINTS.HOSPITALITY_MENU_CATEGORY_ITEM_FILE.UPLOAD}/`, formData, axiosConfig);
};

export const getHospitalityMenuCategoryItemAllergy = (
  hospitalityMenuCategoryItemId: number
): Promise<AxiosResponse<AxDataTableResponse<HospitalityMenuCategoryItemAllergy>>> => {
  const formData = new FormData();
  formData.append("HMenuItem_Id", hospitalityMenuCategoryItemId.toString());

  return api.put(API_ENDPOINTS.HOSPITALITY_MENU_ITEM_ALLERGY.GET, formData, axiosConfig);
};

export const addHospitalityMenuCategoryItemAllergy = (
  hospitalityMenuCategoryItemId: number,
  allergyId: number
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("ItemId", hospitalityMenuCategoryItemId.toString());
  formData.append("AllergyId", allergyId.toString());

  return api.post(API_ENDPOINTS.HOSPITALITY_MENU_ITEM_ALLERGY.ADD, formData, axiosConfig);
};

export const deleteHospitalityMenuCategoryItemAllergy = (
  hospitalityMenuCategoryItemAllergyId: number
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", hospitalityMenuCategoryItemAllergyId.toString());

  const url = `${API_ENDPOINTS.HOSPITALITY_MENU_ITEM_ALLERGY.DELETE}/${hospitalityMenuCategoryItemAllergyId}`;

  return api.post(url, formData, axiosConfig);
};

export const getUser = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<User>>> => {
  const formData = axTableInput(input);

  if (
    typeof dynamicRequestParams?.sortBy !== "undefined" &&
    typeof dynamicRequestParams?.sortDirection !== "undefined"
  ) {
    formData.append("sortBy", dynamicRequestParams.sortBy.toString());
    formData.append("sortDirection", dynamicRequestParams.sortDirection.toString());
  }

  return api.put(API_ENDPOINTS.USER.GET, formData, axiosConfig);
};

export const getUserDetails = (userId: number): Promise<AxiosResponse<UserDetails>> => {
  const formData = new FormData();
  formData.append("Id", userId.toString());

  return api.put(API_ENDPOINTS.USER.GET_DETAILS, formData, axiosConfig);
};

export const getUserFirm = (userId: number): Promise<AxiosResponse<UserFirm[]>> => {
  const formData = new FormData();
  formData.append("Id", userId.toString());

  return api.put(API_ENDPOINTS.USER.GET_USER_FIRM, formData, axiosConfig);
};

export const addUserFirm = (userId: number, firmId: number): Promise<AxiosResponse<UserFirm[]>> => {
  const formData = new FormData();
  formData.append("Id", userId.toString());
  formData.append("Firm_Id", firmId.toString());

  return api.post(API_ENDPOINTS.USER.ADD_USER_FIRM, formData, axiosConfig);
};

export const deleteUserFirm = (userId: number, userFirmId: number): Promise<AxiosResponse<UserFirm[]>> => {
  const formData = new FormData();
  formData.append("Id", userFirmId.toString());

  const url = `${API_ENDPOINTS.USER.DELETE_USER_FIRM}/${userId}`;

  return api.post(url, formData, axiosConfig);
};

export const updateUser = (values: {
  id: number;
  name: string;
  surname: string;
  email: string;
  gender: string;
  mobile: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", values.id.toString());
  formData.append("Name", values.name);
  formData.append("Surname", values.surname);
  formData.append("Email", values.email);
  formData.append("Gender", values.gender);
  formData.append("Mobile", values.mobile);

  const url = `${API_ENDPOINTS.USER.UPDATE}/${values.id}`;

  return api.post(url, formData, axiosConfig);
};

export const addUser = (values: {
  name: string;
  surname: string;
  email: string;
  gender: string;
  mobile: string;
}): Promise<AxiosResponse<number>> => {
  const formData = new FormData();
  formData.append("Name", values.name);
  formData.append("Surname", values.surname);
  formData.append("Email", values.email);
  formData.append("Gender", values.gender);
  formData.append("Mobile", values.mobile);

  return api.post(API_ENDPOINTS.USER.ADD, formData, axiosConfig);
};

export const getAppUserRole = (userId: number): Promise<AxiosResponse<AxDataTableResponse<AppUserRole>>> => {
  const formData = new FormData();
  formData.append("AppUser_Id", userId.toString());

  return api.put(API_ENDPOINTS.APP_USER_ROLE.GET, formData, axiosConfig);
};

export const addAppUserRole = (userId: number, roleId: number): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("AppUser_Id", userId.toString());
  formData.append("Role_Id", roleId.toString());

  return api.post(API_ENDPOINTS.APP_USER_ROLE.ADD, formData, axiosConfig);
};

export const deleteAppUserRole = (userRoleId: number): Promise<AxiosResponse<unknown>> => {
  const url = `${API_ENDPOINTS.APP_USER_ROLE.DELETE}/${userRoleId}`;

  return api.post(url, {}, axiosConfig);
};

export const deleteUser = (userId: number): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", userId.toString());

  const url = `${API_ENDPOINTS.USER.DELETE}/${userId}`;

  return api.post(url, formData, axiosConfig);
};

export const getFirm = (input: AxDataTableRequestInput): Promise<AxiosResponse<AxDataTableResponse<Firm>>> => {
  return api.put(API_ENDPOINTS.FIRM.GET, axTableInput(input), axiosConfig);
};

export const addFirm = (values: {
  name: string;
  systemUsername: string;
  systemPassword: string;
  systemEndPoint: string;
  apiMenu: boolean;
  customerKey: string;
}): Promise<AxiosResponse<{ id: number }>> => {
  const formData = new FormData();
  formData.append("Name", values.name);
  formData.append("SystemUsername", values.systemUsername);
  formData.append("SystemPassword", values.systemPassword);
  formData.append("SystemEndPoint", values.systemEndPoint);
  formData.append("apiMenu", String(values.apiMenu));
  formData.append("CustomerKey", values.customerKey);

  return api.post(API_ENDPOINTS.FIRM.ADD, formData, axiosConfig);
};

export const getFirmDetails = (firmId: number): Promise<AxiosResponse<FirmDetails>> => {
  const formData = new FormData();
  formData.append("Id", firmId.toString());

  return api.put(API_ENDPOINTS.FIRM.GET_DETAILS, formData, axiosConfig);
};

export const updateFirm = (values: {
  id: number;
  name: string;
  systemUsername: string;
  systemPassword: string;
  systemEndPoint: string;
  apiMenu: boolean;
  customerKey: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", values.id.toString());
  formData.append("Name", values.name);
  formData.append("SystemUsername", values.systemUsername);
  formData.append("SystemPassword", values.systemPassword);
  formData.append("SystemEndPoint", values.systemEndPoint);
  formData.append("apiMenu", String(values.apiMenu));
  formData.append("CustomerKey", values.customerKey);

  const url = `${API_ENDPOINTS.FIRM.UPDATE}/${values.id}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteFirm = (firmId: number): Promise<AxiosResponse<FirmDetails>> => {
  const formData = new FormData();
  formData.append("Id", firmId.toString());

  const url = `${API_ENDPOINTS.FIRM.DELETE}/${firmId}`;

  return api.post(url, formData, axiosConfig);
};

export const getFirmDashboard = (firmGuid: string): Promise<AxiosResponse<FirmDashboard>> => {
  const formData = new FormData();
  formData.append("FirmGuid", firmGuid);
  return api.put(API_ENDPOINTS.FIRM.GET_DASHBOARD, formData, axiosConfig);
};

export const updateFirmLicenceDeviceName = (
  firmGuid: string,
  firmLicenceGuid: string,
  deviceName: string
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("FirmLicenceGuid", firmLicenceGuid);
  formData.append("FirmGuid", firmGuid);
  formData.append("DeviceName", deviceName);

  return api.post(API_ENDPOINTS.FIRM_LICENCES.UPDATE_DEVICE_NAME, formData, axiosConfig);
};

export const addFirmLicenceToFirm = (values: {
  totalLicence: number;
  firmId: number;
  expiryDate: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("TotalLicence", values.totalLicence.toString());
  formData.append("Firm_Id", values.firmId.toString());
  formData.append("licenceType", "1");
  formData.append("ExpiryDate", values.expiryDate);

  return api.post(API_ENDPOINTS.FIRM.ADD_LICENCE, formData, axiosConfig);
};

export const deleteFirmLicence = (firmGuid: string, firmLicenceGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("FirmLicenceGuid", firmLicenceGuid);
  formData.append("FirmGuid", firmGuid);

  return api.post(API_ENDPOINTS.FIRM_LICENCES.DELETE, formData, axiosConfig);
};

export const getTemplates = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<TemplateResponse>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.FirmGuid !== "undefined") {
    formData.append("FirmGuid", dynamicRequestParams.FirmGuid.toString());
  }

  return api.put(API_ENDPOINTS.TEMPLATE.GET, formData, axiosConfig);
};

export const addTemplate = (values: {
  firmGuid: string;
  name: string;
  viewport_size: string;
  canvas_data: CanvasData;
}): Promise<AxiosResponse<{ guid: string }>> => {
  const formData = new FormData();
  formData.append("FirmGuid", values.firmGuid);
  formData.append("Name", values.name);
  formData.append("viewport_size", values.viewport_size);
  formData.append("canvas_data", JSON.stringify(values.canvas_data));

  return api.post(API_ENDPOINTS.TEMPLATE.ADD, formData, axiosConfig);
};

export const getTemplateDetails = (firmGuid: string, id: string): Promise<AxiosResponse<TemplateResponse>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("FirmGuid", firmGuid);

  return api.put(API_ENDPOINTS.TEMPLATE.GET_DETAILS, formData, axiosConfig);
};

export const updateTemplate = (values: {
  id: string;
  firmGuid: string;
  name: string;
  viewport_size: string;
  canvas_data: string;
}): Promise<AxiosResponse<{ canvasData: string }>> => {
  const formData = new FormData();
  formData.append("Id", values.id);
  formData.append("FirmGuid", values.firmGuid);
  formData.append("Name", values.name);
  formData.append("viewport_size", values.viewport_size);
  formData.append("canvas_data", values.canvas_data);

  const url = `${API_ENDPOINTS.TEMPLATE.UPDATE}/${values.id}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteTemplateById = (firmGuid: string, id: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("FirmGuid", firmGuid);

  const url = `${API_ENDPOINTS.TEMPLATE.DELETE}/${id}`;

  return api.post(url, formData, axiosConfig);
};

export const getTag = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<Tag>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.Firm_Id !== "undefined") {
    formData.append("Firm_Id", dynamicRequestParams.Firm_Id.toString());
  }

  return api.put(API_ENDPOINTS.TAG.GET, formData, axiosConfig);
};

export const addTag = (values: {
  tagName: string;
  firmGuid: string;
}): Promise<AxiosResponse<{ newDataId: string }>> => {
  const formData = new FormData();
  formData.append("Name", values.tagName);
  formData.append("Firm_Id", values.firmGuid);

  return api.post(API_ENDPOINTS.TAG.ADD, formData, axiosConfig);
};

export const deleteTag = (tagId: string, firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", tagId);
  formData.append("Firm_Id", firmGuid);

  const url = `${API_ENDPOINTS.TAG.DELETE}?Id=${tagId}&Firm_Id=${firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const updateTag = (values: {
  id: string;
  tagName: string;
  firmGuid: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Firm_Id", values.firmGuid);
  formData.append("Id", values.id);
  formData.append("Name", values.tagName);

  const url = `${API_ENDPOINTS.TAG.UPDATE}?Id=${values.id}&Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const getTagDetails = (tagId: string, firmGuid: string): Promise<AxiosResponse<Tag>> => {
  const formData = new FormData();
  formData.append("Id", tagId);
  formData.append("Firm_Id", firmGuid);
  return api.put(API_ENDPOINTS.TAG.GET_DETAILS, formData, axiosConfig);
};

export const getScreenSize = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<ScreenSize>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.Firm_Id !== "undefined") {
    formData.append("Firm_Id", dynamicRequestParams.Firm_Id.toString());
  }
  if (
    typeof dynamicRequestParams?.sortBy !== "undefined" &&
    typeof dynamicRequestParams?.sortDirection !== "undefined"
  ) {
    formData.append("sortBy", dynamicRequestParams.sortBy.toString());
    formData.append("sortDirection", dynamicRequestParams.sortDirection.toString());
  }
  return api.put(API_ENDPOINTS.SCREEN_SIZE.GET, formData, axiosConfig);
};

export const addScreenSize = (values: {
  name: string;
  width: number;
  height: number;
  firmGuid: string;
}): Promise<AxiosResponse<{ newDataId: string }>> => {
  const formData = new FormData();
  formData.append("Name", values.name);
  formData.append("Width", values.width.toString());
  formData.append("Height", values.height.toString());
  formData.append("Firm_Id", values.firmGuid.toString());
  return api.post(API_ENDPOINTS.SCREEN_SIZE.ADD, formData, axiosConfig);
};

export const deleteScreenSize = (id: string, firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmGuid);
  return api.post(API_ENDPOINTS.SCREEN_SIZE.DELETE, formData, axiosConfig);
};

export const updateScreenSize = (values: {
  id: string;
  name: string;
  width: number;
  height: number;
  firmGuid: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Name", values.name);
  formData.append("Width", values.width.toString());
  formData.append("Height", values.height.toString());
  formData.append("Firm_Id", values.firmGuid);
  const url = `${API_ENDPOINTS.SCREEN_SIZE.UPDATE}?Id=${values.id}&Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const getScreenSizeDetails = (id: string, firmGuid: string): Promise<AxiosResponse<ScreenSizeDetails>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmGuid);
  return api.put(API_ENDPOINTS.SCREEN_SIZE.GET_DETAILS, formData, axiosConfig);
};

export const getPlayer = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<Player>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.Firm_Id !== "undefined") {
    formData.append("Firm_Id", dynamicRequestParams.Firm_Id.toString());
  }
  if (
    typeof dynamicRequestParams?.sortBy !== "undefined" &&
    typeof dynamicRequestParams?.sortDirection !== "undefined"
  ) {
    formData.append("sortBy", dynamicRequestParams.sortBy.toString());
    formData.append("sortDirection", dynamicRequestParams.sortDirection.toString());
  }
  if (typeof dynamicRequestParams?.ScreenSizeGuid !== "undefined") {
    formData.append("ScreenSizeGuid", dynamicRequestParams.ScreenSizeGuid.toString());
  }
  if (typeof dynamicRequestParams?.TagGuid !== "undefined") {
    formData.append("TagGuid", dynamicRequestParams.TagGuid.toString());
  }
  if (typeof dynamicRequestParams?.healthStatus !== "undefined" && dynamicRequestParams.healthStatus !== false) {
    formData.append("healthStatus", dynamicRequestParams.healthStatus.toString());
  }

  return api.put(API_ENDPOINTS.PLAYER.GET, formData, axiosConfig);
};

export const addPlayer = (values: {
  name: string;
  screenSizeId: number;
  timeZone: string;
  channelId?: number | null;
  contentGroupId?: number | null;
  contentId?: number | null;
  defaultContentId?: number | null;
  firmGuid: string;
  areaId: number;
  latitude?: number | null;
  longitude?: number | null;
  healthSequence: number;
}): Promise<AxiosResponse<{ newDataId: string }>> => {
  const formData = new FormData();
  formData.append("Name", values.name);
  formData.append("ScreenSize_Id", values.screenSizeId.toString());
  formData.append("Timezone", values.timeZone);
  formData.append("Channel_Id", values.channelId?.toString() || "");
  formData.append("ContentGroup_Id", values.contentGroupId?.toString() || "");
  formData.append("Content_Id", values.contentId?.toString() || "");
  formData.append("DefaultContent_Id", values.defaultContentId?.toString() || "");
  formData.append("Firm_Id", values.firmGuid);
  formData.append("Area_Id", values.areaId.toString());
  formData.append("Latitude", values.latitude?.toString() || "");
  formData.append("Longitude", values.longitude?.toString() || "");
  formData.append("HealthSequence", values.healthSequence.toString());
  return api.post(API_ENDPOINTS.PLAYER.ADD, formData, axiosConfig);
};

export const updatePlayer = (values: {
  id: string;
  name: string;
  screenSizeId: number;
  timeZone: string;
  channelId?: number | null;
  contentGroupId?: number | null;
  contentId?: number | null;
  defaultContentId?: number | null;
  firmGuid: string;
  areaId: number;
  latitude?: number | null;
  longitude?: number | null;
  healthSequence: number;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Name", values.name);
  formData.append("ScreenSize_Id", values.screenSizeId.toString());
  formData.append("Timezone", values.timeZone);
  formData.append("Channel_Id", values.channelId?.toString() || "");
  formData.append("ContentGroup_Id", values.contentGroupId?.toString() || "");
  formData.append("Content_Id", values.contentId?.toString() || "");
  formData.append("DefaultContent_Id", values.defaultContentId?.toString() || "");
  formData.append("Firm_Id", values.firmGuid);
  formData.append("Area_Id", values.areaId?.toString());
  formData.append("Latitude", values.latitude?.toString() || "");
  formData.append("Longitude", values.longitude?.toString() || "");
  formData.append("HealthSequence", values.healthSequence?.toString() || "");

  const url = `${API_ENDPOINTS.PLAYER.UPDATE}?Id=${values.id}&Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const addPlayerTag = (values: {
  firmGuid: string;
  playerIntId: number;
  tagIntId: number;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Firm_Id", values.firmGuid);
  formData.append("Player_Id", values.playerIntId.toString());
  formData.append("Tag_Id", values.tagIntId.toString());

  const url = `${API_ENDPOINTS.PLAYER_TAG.ADD}?Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const deletePlayerTag = (values: { firmGuid: string; playerTagId: string }): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Firm_Id", values.firmGuid);

  const url = `${API_ENDPOINTS.PLAYER_TAG.DELETE}?Id=${values.playerTagId}&Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const getPlayerTags = (
  firmGuid: string,
  playerGuid: string
): Promise<AxiosResponse<AxDataTableResponse<PlayerTag>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  formData.append("Firm_Id", firmGuid);
  formData.append("Player_Id", playerGuid);
  return api.put(API_ENDPOINTS.PLAYER_TAG.GET, formData, axiosConfig);
};

export const assignLicenceToPlayer = (values: {
  firmGuid: string;
  playerId: string;
  firmLicenceId: number;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Firm_Id", values.firmGuid);
  formData.append("Id", values.playerId);
  formData.append("FirmLicence_Id", values.firmLicenceId.toString());
  return api.post(API_ENDPOINTS.PLAYER.ASSIGN_LICENCE, formData, axiosConfig);
};

export const deletePlayer = (id: string, firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmGuid);
  return api.post(API_ENDPOINTS.PLAYER.DELETE, formData, axiosConfig);
};

export const getPlayerDetails = (id: string, firmGuid: string): Promise<AxiosResponse<PlayerDetails>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmGuid);
  return api.put(API_ENDPOINTS.PLAYER.GET_DETAILS, formData, axiosConfig);
};

export const getChannel = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<Channel>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.Firm_Id !== "undefined") {
    formData.append("Firm_Id", dynamicRequestParams.Firm_Id.toString());
  }
  if (typeof dynamicRequestParams?.TagGuid !== "undefined") {
    formData.append("TagGuid", dynamicRequestParams.TagGuid.toString());
  }
  if (typeof dynamicRequestParams?.ScreenSizeGuid !== "undefined") {
    formData.append("ScreenSizeGuid", dynamicRequestParams.ScreenSizeGuid.toString());
  }
  return api.put(API_ENDPOINTS.CHANNEL.GET, formData, axiosConfig);
};

export const addChannel = (values: {
  name: string;
  firmGuid: string;
  isCombineAndShuffle: boolean;
  defaultContentId?: number;
  screenSizeId: number;
}): Promise<AxiosResponse<{ newDataId: string }>> => {
  const formData = new FormData();
  formData.append("Name", values.name);
  formData.append("Firm_Id", values.firmGuid.toString());
  formData.append("IsCombineAndShuffle", values.isCombineAndShuffle.toString());
  formData.append("DefaultContent_Id", values.defaultContentId?.toString() || "");
  formData.append("ScreenSize_Id", values.screenSizeId?.toString() || "");

  return api.post(API_ENDPOINTS.CHANNEL.ADD, formData, axiosConfig);
};

export const deleteChannel = (channelId: string, firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", channelId);
  formData.append("Firm_Id", firmGuid);

  return api.post(API_ENDPOINTS.CHANNEL.DELETE, formData, axiosConfig);
};

export const updateChannel = (values: {
  id: string;
  name: string;
  firmGuid: string;
  isCombineAndShuffle: boolean;
  defaultContentId?: number;
  screenSizeId: number;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", values.id);
  formData.append("Name", values.name);
  formData.append("Firm_Id", values.firmGuid);
  formData.append("IsCombineAndShuffle", values.isCombineAndShuffle.toString());
  formData.append("DefaultContent_Id", values.defaultContentId?.toString() || "");
  formData.append("ScreenSize_Id", values.screenSizeId.toString());

  const url = `${API_ENDPOINTS.CHANNEL.UPDATE}?Id=${values.id}&Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const addChannelTag = (values: {
  firmGuid: string;
  channelIntId: number;
  tagIntId: number;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Firm_Id", values.firmGuid);
  formData.append("Channel_Id", values.channelIntId.toString());
  formData.append("Tag_Id", values.tagIntId.toString());

  const url = `${API_ENDPOINTS.CHANNEL_TAG.ADD}?Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteChannelTag = (values: {
  firmGuid: string;
  channelTagId: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Firm_Id", values.firmGuid);

  const url = `${API_ENDPOINTS.CHANNEL_TAG.DELETE}?Id=${values.channelTagId}&Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const getChannelTags = (
  firmGuid: string,
  channelGuid: string
): Promise<AxiosResponse<AxDataTableResponse<ChannelTag>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  formData.append("Firm_Id", firmGuid);
  formData.append("Channel_Id", channelGuid);
  return api.put(API_ENDPOINTS.CHANNEL_TAG.GET, formData, axiosConfig);
};

export const getChannelDetails = (channelId: string, firmGuid: string): Promise<AxiosResponse<ChannelDetails>> => {
  const formData = new FormData();
  formData.append("Id", channelId);
  formData.append("Firm_Id", firmGuid);
  return api.put(API_ENDPOINTS.CHANNEL.GET_DETAILS, formData, axiosConfig);
};

export const addChannelDetailItem = (values: {
  channelId: number;
  periodStart: string;
  periodEnd: string;
  isActive: boolean;
  order?: number;
  firmId: string;
}): Promise<AxiosResponse<{ newDataId: string }>> => {
  const formData = new FormData();
  formData.append("Channel_Id", values.channelId.toString());
  formData.append("PeriodStart", values.periodStart);
  formData.append("PeriodEnd", values.periodEnd);
  formData.append("IsActive", values.isActive.toString());
  formData.append("Order", values.order?.toString() || "");
  formData.append("Firm_Id", values.firmId);

  return api.post(API_ENDPOINTS.CHANNEL_DETAIL.ADD, formData, axiosConfig);
};

export const updateChannelDetailItem = (values: {
  channelDetailItemId: string;
  channelId: number;
  periodStart: string;
  periodEnd: string;
  isActive: boolean;
  order?: number;
  firmId: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", values.channelDetailItemId);
  formData.append("Channel_Id", values.channelId.toString());
  formData.append("PeriodStart", values.periodStart);
  formData.append("PeriodEnd", values.periodEnd);
  formData.append("IsActive", values.isActive.toString());
  formData.append("Order", values.order?.toString() || "");
  formData.append("Firm_Id", values.firmId);

  return api.post(API_ENDPOINTS.CHANNEL_DETAIL.UPDATE, formData, axiosConfig);
};

export const getChannelDetailItems = (
  channelId: string,
  firmId: string
): Promise<AxiosResponse<AxDataTableResponse<ChannelDetailItem>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  formData.append("Channel_Id", channelId);
  formData.append("Firm_Id", firmId);
  return api.put(API_ENDPOINTS.CHANNEL_DETAIL.GET, formData, axiosConfig);
};

export const deleteChannelDetailItem = (id: string, firmId: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmId);
  return api.post(API_ENDPOINTS.CHANNEL_DETAIL.DELETE, formData, axiosConfig);
};

export const addChannelDetailDate = (values: {
  channelDetailId: number;
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
  firmGuid: string;
}): Promise<AxiosResponse<{ newDataId: string }>> => {
  const formData = new FormData();
  formData.append("ChannelDetail_Id", values.channelDetailId.toString());
  formData.append("DayOfWeek", values.dayOfWeek.toString());
  formData.append("StartTime", values.startTime);
  formData.append("EndTime", values.endTime);
  formData.append("Firm_Id", values.firmGuid);

  const url = `${API_ENDPOINTS.CHANNEL_DETAIL_DATE.ADD}?Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const getChannelDetailDate = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<ChannelDetailDate>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.Firm_Id !== "undefined") {
    formData.append("Firm_Id", dynamicRequestParams.Firm_Id.toString());
  }
  if (typeof dynamicRequestParams?.ChannelDetail_Id !== "undefined") {
    formData.append("ChannelDetail_Id", dynamicRequestParams.ChannelDetail_Id.toString());
  }
  return api.put(API_ENDPOINTS.CHANNEL_DETAIL_DATE.GET, formData, axiosConfig);
};

export const updateChannelDetailDate = (values: {
  firmGuid: string;
  channelDetailItemId: string;
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", values.channelDetailItemId);
  formData.append("DayOfWeek", values.dayOfWeek.toString());
  formData.append("StartTime", values.startTime);
  formData.append("EndTime", values.endTime);
  formData.append("Firm_Id", values.firmGuid);

  const url = `${API_ENDPOINTS.CHANNEL_DETAIL_DATE.UPDATE}?Id=${values.channelDetailItemId}&Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteChannelDetailDate = (id: string, firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmGuid);
  return api.post(API_ENDPOINTS.CHANNEL_DETAIL_DATE.DELETE, formData, axiosConfig);
};

export const addContentTag = (values: {
  firmGuid: string;
  contentIntId: number;
  tagIntId: number;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Firm_Id", values.firmGuid);
  formData.append("Content_Id", values.contentIntId.toString());
  formData.append("Tag_Id", values.tagIntId.toString());

  const url = `${API_ENDPOINTS.CONTENT_TAG.ADD}?Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteContentTag = (values: {
  firmGuid: string;
  contentTagId: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Firm_Id", values.firmGuid);

  const url = `${API_ENDPOINTS.CONTENT_TAG.DELETE}?Id=${values.contentTagId}&Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const getContentTags = (
  firmGuid: string,
  contentGuid: string
): Promise<AxiosResponse<AxDataTableResponse<ContentTag>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  formData.append("Firm_Id", firmGuid);
  formData.append("Content_Id", contentGuid);
  return api.put(API_ENDPOINTS.CONTENT_TAG.GET, formData, axiosConfig);
};

export const getArea = (firmGuid: string): Promise<AxiosResponse<AxDataTableResponse<Area>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  formData.append("Firm_Id", firmGuid);
  return api.put(API_ENDPOINTS.AREA.GET, formData, axiosConfig);
};

export const getAreaDetails = (id: string, firmGuid: string): Promise<AxiosResponse<AreaDetails>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmGuid);
  return api.put(API_ENDPOINTS.AREA.GET_DETAILS, formData, axiosConfig);
};

export const addArea = (values: {
  name: string;
  firmGuid: string;
  areaTypeId: number;
  motherAreaintId?: number;
  timezone: string;
  defaultContentId?: number | null;
  overwriteSub: boolean;
}): Promise<AxiosResponse<{ newDataId: string }>> => {
  const formData = new FormData();
  formData.append("Name", values.name);
  formData.append("Firm_Id", values.firmGuid);
  if (values.areaTypeId) {
    formData.append("AreaType_Id", values.areaTypeId.toString());
  }
  formData.append("MotherArea_Id", values.motherAreaintId?.toString() || "");
  formData.append("Timezone", values.timezone);
  formData.append("DefaultContent_Id", values.defaultContentId?.toString() || "");
  formData.append("OverwriteSub", values.overwriteSub.toString());
  const url = `${API_ENDPOINTS.AREA.ADD}?Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteArea = (firmGuid: string, id: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmGuid);

  const url = `${API_ENDPOINTS.AREA.DELETE}?Id=${id}&Firm_Id=${firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const updateArea = (values: {
  id: string;
  firmGuid: string;
  name: string;
  motherAreaintId?: number;
  timezone: string;
  defaultContentId?: number | null;
  areaTypeId: number;
  overwriteSub: boolean;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", values.id);
  formData.append("Name", values.name);
  formData.append("Firm_Id", values.firmGuid);
  formData.append("MotherArea_Id", values.motherAreaintId?.toString() || "");
  formData.append("Timezone", values.timezone);
  formData.append("DefaultContent_Id", values.defaultContentId?.toString() || "");
  formData.append("AreaType_Id", values.areaTypeId.toString());
  formData.append("OverwriteSub", values.overwriteSub.toString());

  const url = `${API_ENDPOINTS.AREA.UPDATE}?Id=${values.id}&Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const getCampaign = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<Campaign>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.Firm_Id !== "undefined") {
    formData.append("Firm_Id", dynamicRequestParams.Firm_Id.toString());
  }
  if (typeof dynamicRequestParams?.TagGuid !== "undefined") {
    formData.append("TagGuid", dynamicRequestParams.TagGuid.toString());
  }
  return api.put(API_ENDPOINTS.CAMPAIGN.GET, formData, axiosConfig);
};

export const addCampaignTag = (values: {
  firmGuid: string;
  campaignIntId: number;
  tagIntId: number;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Firm_Id", values.firmGuid);
  formData.append("Campaign_Id", values.campaignIntId.toString());
  formData.append("Tag_Id", values.tagIntId.toString());

  const url = `${API_ENDPOINTS.CAMPAIGN_TAG.ADD}?Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteCampaignTag = (values: {
  firmGuid: string;
  campaignTagId: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Firm_Id", values.firmGuid);

  const url = `${API_ENDPOINTS.CAMPAIGN_TAG.DELETE}?Id=${values.campaignTagId}&Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const getCampaignTags = (
  firmGuid: string,
  campaignGuid: string
): Promise<AxiosResponse<AxDataTableResponse<CampaignTag>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  formData.append("Firm_Id", firmGuid);
  formData.append("Campaign_Id", campaignGuid);
  return api.put(API_ENDPOINTS.CAMPAIGN_TAG.GET, formData, axiosConfig);
};

export const getCampaignDetails = (campaignId: string, firmGuid: string): Promise<AxiosResponse<CampaignDetails>> => {
  const formData = new FormData();
  formData.append("Id", campaignId);
  formData.append("Firm_Id", firmGuid);
  return api.put(API_ENDPOINTS.CAMPAIGN.GET_DETAILS, formData, axiosConfig);
};

export const addCampaign = (values: {
  name: string;
  firmGuid: string;
  isCombineAndShuffle: boolean;
  defaultContentId?: number;
  campaignType: CampaignType;
}): Promise<AxiosResponse<{ newDataId: string }>> => {
  const formData = new FormData();
  formData.append("Name", values.name);
  formData.append("Firm_Id", values.firmGuid.toString());
  formData.append("IsCombineAndShuffle", values.isCombineAndShuffle.toString());
  formData.append("DefaultContent_Id", values.defaultContentId?.toString() || "");
  formData.append("CampaignType", values.campaignType.toString());
  return api.post(API_ENDPOINTS.CAMPAIGN.ADD, formData, axiosConfig);
};

export const deleteCampaign = (campaignId: string, firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", campaignId);
  formData.append("Firm_Id", firmGuid);

  return api.post(API_ENDPOINTS.CAMPAIGN.DELETE, formData, axiosConfig);
};

export const updateCampaign = (values: {
  id: string;
  name: string;
  firmGuid: string;
  isCombineAndShuffle: boolean;
  defaultContentId?: number;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", values.id);
  formData.append("Name", values.name);
  formData.append("Firm_Id", values.firmGuid);
  formData.append("IsCombineAndShuffle", values.isCombineAndShuffle.toString());
  formData.append("DefaultContent_Id", values.defaultContentId?.toString() || "");

  const url = `${API_ENDPOINTS.CAMPAIGN.UPDATE}?Id=${values.id}&Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const addCampaignDetailItem = (values: {
  campaignId: number;
  periodStart: string;
  periodEnd: string;
  isActive: boolean;
  firmId: string;
  contentGroupId?: number | null;
}): Promise<AxiosResponse<{ newDataId: string }>> => {
  const formData = new FormData();
  formData.append("Campaign_Id", values.campaignId.toString());
  formData.append("PeriodStart", values.periodStart);
  formData.append("PeriodEnd", values.periodEnd);
  formData.append("IsActive", values.isActive.toString());
  formData.append("Firm_Id", values.firmId);

  if (values.contentGroupId) {
    formData.append("ContentGroup_Id", values.contentGroupId.toString());
  }

  return api.post(API_ENDPOINTS.CAMPAIGN_DETAIL.ADD, formData, axiosConfig);
};

export const updateCampaignDetailItem = (values: {
  campaignDetailItemId: string;
  campaignId: number;
  periodStart: string;
  periodEnd: string;
  isActive: boolean;
  firmId: string;
  contentGroupId?: number | null;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", values.campaignDetailItemId);
  formData.append("Campaign_Id", values.campaignId.toString());
  formData.append("PeriodStart", values.periodStart);
  formData.append("PeriodEnd", values.periodEnd);
  formData.append("IsActive", values.isActive.toString());
  formData.append("Firm_Id", values.firmId);

  if (values.contentGroupId) {
    formData.append("ContentGroup_Id", values.contentGroupId.toString());
  }

  return api.post(API_ENDPOINTS.CAMPAIGN_DETAIL.UPDATE, formData, axiosConfig);
};

export const getCampaignDetailItems = (
  campaignId: string,
  firmId: string
): Promise<AxiosResponse<AxDataTableResponse<CampaignDetailItem>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  formData.append("Campaign_Id", campaignId);
  formData.append("Firm_Id", firmId);
  return api.put(API_ENDPOINTS.CAMPAIGN_DETAIL.GET, formData, axiosConfig);
};

export const deleteCampaignDetailItem = (id: string, firmId: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmId);
  return api.post(API_ENDPOINTS.CAMPAIGN_DETAIL.DELETE, formData, axiosConfig);
};

export const addCampaignDetailDate = (values: {
  campaignDetailId: number;
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
  firmGuid: string;
}): Promise<AxiosResponse<{ newDataId: string }>> => {
  const formData = new FormData();
  formData.append("CampaignDetail_Id", values.campaignDetailId.toString());
  formData.append("DayOfWeek", values.dayOfWeek.toString());
  formData.append("StartTime", values.startTime);
  formData.append("EndTime", values.endTime);
  formData.append("Firm_Id", values.firmGuid);

  const url = `${API_ENDPOINTS.CAMPAIGN_DETAIL_DATE.ADD}?Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const getCampaignDetailDate = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<CampaignDetailDate>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.Firm_Id !== "undefined") {
    formData.append("Firm_Id", dynamicRequestParams.Firm_Id.toString());
  }
  if (typeof dynamicRequestParams?.CampaignDetail_Id !== "undefined") {
    formData.append("CampaignDetail_Id", dynamicRequestParams.CampaignDetail_Id.toString());
  }
  return api.put(API_ENDPOINTS.CAMPAIGN_DETAIL_DATE.GET, formData, axiosConfig);
};

export const updateCampaignDetailDate = (values: {
  firmGuid: string;
  campaignDetailItemId: string;
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", values.campaignDetailItemId);
  formData.append("DayOfWeek", values.dayOfWeek.toString());
  formData.append("StartTime", values.startTime);
  formData.append("EndTime", values.endTime);
  formData.append("Firm_Id", values.firmGuid);

  const url = `${API_ENDPOINTS.CAMPAIGN_DETAIL_DATE.UPDATE}?Id=${values.campaignDetailItemId}&Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteCampaignDetailDate = (id: string, firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmGuid);
  return api.post(API_ENDPOINTS.CAMPAIGN_DETAIL_DATE.DELETE, formData, axiosConfig);
};

export const addPlayerCampaign = (values: {
  playerId: number;
  campaignId: number;
  firmGuid: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Player_Id", values.playerId.toString());
  formData.append("Campaign_Id", values.campaignId.toString());
  formData.append("Firm_Id", values.firmGuid);

  const url = `${API_ENDPOINTS.PLAYER_CAMPAIGN.ADD}?Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const deletePlayerCampaign = (id: string, firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmGuid);
  return api.post(API_ENDPOINTS.PLAYER_CAMPAIGN.DELETE, formData, axiosConfig);
};

export const getPlayerCampaign = (
  playerId: string,
  firmGuid: string
): Promise<AxiosResponse<AxDataTableResponse<PlayerCampaign>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  formData.append("Player_Id", playerId);
  formData.append("Firm_Id", firmGuid);
  return api.put(API_ENDPOINTS.PLAYER_CAMPAIGN.GET, formData, axiosConfig);
};

export const addAreaCampaign = (values: {
  areaId: number;
  campaignId: number;
  firmGuid: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Area_Id", values.areaId.toString());
  formData.append("Campaign_Id", values.campaignId.toString());
  formData.append("Firm_Id", values.firmGuid);

  const url = `${API_ENDPOINTS.AREA_CAMPAIGN.ADD}?Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteAreaCampaign = (id: string, firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmGuid);
  return api.post(API_ENDPOINTS.AREA_CAMPAIGN.DELETE, formData, axiosConfig);
};

export const getAreaCampaign = (
  areaId: string,
  firmGuid: string
): Promise<AxiosResponse<AxDataTableResponse<AreaCampaign>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  formData.append("Area_Id", areaId);
  formData.append("Firm_Id", firmGuid);
  return api.put(API_ENDPOINTS.AREA_CAMPAIGN.GET, formData, axiosConfig);
};

export const addContentGroupTag = (values: {
  firmGuid: string;
  contentGroupIntId: number;
  tagIntId: number;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Firm_Id", values.firmGuid);
  formData.append("ContentGroup_Id", values.contentGroupIntId.toString());
  formData.append("Tag_Id", values.tagIntId.toString());

  const url = `${API_ENDPOINTS.CONTENT_GROUP_TAG.ADD}?Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteContentGroupTag = (values: {
  firmGuid: string;
  contentGroupTagId: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Firm_Id", values.firmGuid);

  const url = `${API_ENDPOINTS.CONTENT_GROUP_TAG.DELETE}?Id=${values.contentGroupTagId}&Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const getContentGroupTags = (
  firmGuid: string,
  contentGroupGuid: string
): Promise<AxiosResponse<AxDataTableResponse<ContentGroupTag>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  formData.append("Firm_Id", firmGuid);
  formData.append("ContentGroup_Id", contentGroupGuid);
  return api.put(API_ENDPOINTS.CONTENT_GROUP_TAG.GET, formData, axiosConfig);
};

export const getAreaType = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<AreaType>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.Firm_Id !== "undefined") {
    formData.append("Firm_Id", dynamicRequestParams?.Firm_Id?.toString());
  }
  return api.put(API_ENDPOINTS.AREA_TYPE.GET, formData, axiosConfig);
};

export const getAreaTypeDetails = (areaTypeId: string, firmGuid: string): Promise<AxiosResponse<AreaTypeDetails>> => {
  const formData = new FormData();
  formData.append("Id", areaTypeId.toString());
  formData.append("Firm_Id", firmGuid);

  return api.put(API_ENDPOINTS.AREA_TYPE.GET_DETAILS, formData, axiosConfig);
};

export const addAreaType = (values: {
  firmGuid: string;
  name: string;
  color: string;
  extra: string;
}): Promise<AxiosResponse<{ newDataId: string }>> => {
  const formData = new FormData();
  formData.append("Name", values.name);
  formData.append("Color", values.color);
  formData.append("Extra", values.extra);

  const url = `${API_ENDPOINTS.AREA_TYPE.ADD}?Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteAreaType = (areaTypeId: string, firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", areaTypeId);
  formData.append("Firm_Id", firmGuid);

  const url = `${API_ENDPOINTS.AREA_TYPE.DELETE}?Id=${areaTypeId}&Firm_Id=${firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const updateAreaType = (values: {
  id: string;
  firmGuid: string;
  name: string;
  color: string;
  extra: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", values.id);
  formData.append("Firm_Id", values.firmGuid);
  formData.append("Name", values.name);
  formData.append("Color", values.color);
  formData.append("Extra", values.extra);

  const url = `${API_ENDPOINTS.AREA_TYPE.UPDATE}?Id=${values.id}&Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const getAreaContent = (
  areaId: string,
  firmGuid: string
): Promise<AxiosResponse<AxDataTableResponse<AreaContent>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  formData.append("Area_Id", areaId);
  formData.append("Firm_Id", firmGuid);
  return api.put(API_ENDPOINTS.AREA_CONTENT.GET, formData, axiosConfig);
};

export const addAreaContent = (values: {
  areaId: number;
  contentId?: number;
  contentGroupId?: number;
  firmGuid: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Area_Id", values.areaId.toString());
  formData.append("Content_Id", values.contentId?.toString() || "");
  formData.append("ContentGroup_Id", values.contentGroupId?.toString() || "");
  formData.append("Firm_Id", values.firmGuid);

  const url = `${API_ENDPOINTS.AREA_CONTENT.ADD}?Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteAreaContent = (id: string, firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmGuid);
  return api.post(API_ENDPOINTS.AREA_CONTENT.DELETE, formData, axiosConfig);
};

export const getCampaignDetailContent = (
  campaignDetailId: string,
  firmGuid: string
): Promise<AxiosResponse<AxDataTableResponse<CampaignDetailItemContent>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  formData.append("CampaignDetail_Id", campaignDetailId);
  formData.append("Firm_Id", firmGuid);
  return api.put(API_ENDPOINTS.CAMPAIGN_DETAIL_CONTENT.GET, formData, axiosConfig);
};

export const addCampaignDetailContent = (values: {
  campaignDetailId: number;
  contentId?: number;
  firmGuid: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("CampaignDetail_Id", values.campaignDetailId.toString());
  formData.append("Content_Id", values.contentId?.toString() || "");
  formData.append("Firm_Id", values.firmGuid);
  return api.post(API_ENDPOINTS.CAMPAIGN_DETAIL_CONTENT.ADD, formData, axiosConfig);
};

export const deleteCampaignDetailContent = (id: string, firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmGuid);
  return api.post(API_ENDPOINTS.CAMPAIGN_DETAIL_CONTENT.DELETE, formData, axiosConfig);
};

export const getChannelDetailContent = (
  channelDetailId: string,
  firmGuid: string
): Promise<AxiosResponse<AxDataTableResponse<ChannelDetailItemContent>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  formData.append("ChannelDetail_Id", channelDetailId);
  formData.append("Firm_Id", firmGuid);
  return api.put(API_ENDPOINTS.CHANNEL_DETAIL_CONTENT.GET, formData, axiosConfig);
};

export const addChannelDetailContent = (values: {
  channelDetailId: number;
  contentId?: number;
  contentGroupId?: number;
  firmGuid: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("ChannelDetail_Id", values.channelDetailId.toString());
  if (values.contentId) {
    formData.append("Content_Id", values.contentId.toString());
  }
  if (values.contentGroupId) {
    formData.append("ContentGroup_Id", values.contentGroupId.toString());
  }
  formData.append("Firm_Id", values.firmGuid);
  return api.post(API_ENDPOINTS.CHANNEL_DETAIL_CONTENT.ADD, formData, axiosConfig);
};

export const deleteChannelDetailContent = (id: string, firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmGuid);
  return api.post(API_ENDPOINTS.CHANNEL_DETAIL_CONTENT.DELETE, formData, axiosConfig);
};

export const getAllPlayerDetails = (
  firmGuid: string,
  playerGuid: string
): Promise<AxiosResponse<DSPlayerOBj<string>>> => {
  const formData = new FormData();
  formData.append("Firm_Id", firmGuid);
  formData.append("Id", playerGuid);
  const url = `${API_ENDPOINTS.PLAYER.GET_ALL_PLAYER_DETAILS}?Firm_Id=${firmGuid}&Id=${playerGuid}`;
  return api.put(url, formData, axiosConfig);
};

export const getApi = (): Promise<AxiosResponse<AxDataTableResponse<Api>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  return api.put(API_ENDPOINTS.API.GET, formData, axiosConfig);
};

export const addMenuApi = (menuId: number, apiId: number): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Menu_Id", menuId.toString());
  formData.append("Api_Id", apiId.toString());
  return api.post(API_ENDPOINTS.MENU_API.ADD, formData, axiosConfig);
};

export const getMenuApi = (menuId: number): Promise<AxiosResponse<AxDataTableResponse<MenuApi>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  formData.append("Menu_Id", menuId.toString());
  return api.put(API_ENDPOINTS.MENU_API.GET, formData, axiosConfig);
};

export const deleteMenuApi = (menuApiId: number): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", menuApiId.toString());
  const url = `${API_ENDPOINTS.MENU_API.DELETE}/${menuApiId}`;
  return api.post(url, formData, axiosConfig);
};

export const getStockTemplates = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<StockTemplateResponse>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.category_name !== "undefined") {
    formData.delete("search");
    formData.append("search", dynamicRequestParams.category_name.toString());
  }
  return api.put(API_ENDPOINTS.STOCK_TEMPLATE.GET, formData, axiosConfig);
};

export const addStockTemplate = (values: {
  firmGuid: string;
  name: string;
  viewport_size: string;
  canvas_data: CanvasData;
  categoryName: string;
}): Promise<AxiosResponse<{ guid: string }>> => {
  const formData = new FormData();
  formData.append("FirmGuid", values.firmGuid);
  formData.append("Name", values.name);
  formData.append("viewport_size", values.viewport_size);
  formData.append("canvas_data", JSON.stringify(values.canvas_data));
  formData.append("category_name", values.categoryName);
  return api.post(API_ENDPOINTS.STOCK_TEMPLATE.ADD, formData, axiosConfig);
};

export const getStockTemplateDetails = (
  firmGuid: string,
  id: string
): Promise<AxiosResponse<StockTemplateResponse>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("FirmGuid", firmGuid);

  return api.put(API_ENDPOINTS.STOCK_TEMPLATE.GET_DETAILS, formData, axiosConfig);
};

export const updateStockTemplate = (values: {
  id: string;
  firmGuid: string;
  name: string;
  viewport_size: string;
  canvas_data: string;
  categoryName: string;
}): Promise<AxiosResponse<{ canvasData: string }>> => {
  const formData = new FormData();
  formData.append("Id", values.id);
  formData.append("FirmGuid", values.firmGuid);
  formData.append("Name", values.name);
  formData.append("viewport_size", values.viewport_size);
  formData.append("canvas_data", values.canvas_data);
  formData.append("category_name", values.categoryName);

  const url = `${API_ENDPOINTS.STOCK_TEMPLATE.UPDATE}/${values.id}`;

  return api.post(url, formData, axiosConfig);
};

export const deleteStockTemplateById = (firmGuid: string, id: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("FirmGuid", firmGuid);

  const url = `${API_ENDPOINTS.STOCK_TEMPLATE.DELETE}/${id}`;

  return api.post(url, formData, axiosConfig);
};

export const getBlogPost = (input: AxDataTableRequestInput): Promise<AxiosResponse<AxDataTableResponse<BlogPost>>> => {
  const formData = axTableInput(input);

  return api.put(API_ENDPOINTS.BLOG_POST.GET, formData, axiosConfig);
};

export const getBlogPostDetails = (id: string): Promise<AxiosResponse<BlogPostDetails>> => {
  const formData = new FormData();
  formData.append("Id", id.toString());

  return api.put(API_ENDPOINTS.BLOG_POST.GET_DETAILS, formData, axiosConfig);
};

export const addBlogPost = (title: string, content: string): Promise<AxiosResponse<{ guid: string }>> => {
  const formData = new FormData();
  formData.append("Title", title);
  formData.append("Content", content);

  return api.post(API_ENDPOINTS.BLOG_POST.ADD, formData, axiosConfig);
};

export const deleteBlogPost = (blogPostId: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", blogPostId.toString());

  const url = `${API_ENDPOINTS.BLOG_POST.DELETE}/${blogPostId}`;

  return api.post(url, formData, axiosConfig);
};

export const updateBlogPost = (blogPostId: string, title: string, content: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", blogPostId.toString());
  formData.append("Title", title);
  formData.append("Content", content);

  const url = `${API_ENDPOINTS.BLOG_POST.UPDATE}/${blogPostId}`;

  return api.post(url, formData, axiosConfig);
};

// Sync player to the player sync status
export const syncPlayer = (
  tempSessionCode: string,
  playerId: number
): Promise<AxiosResponse<{ isSuccess: boolean; data: PlayerSyncStatus }>> => {
  const formData = new FormData();
  formData.append("tempSessionCode", tempSessionCode);
  formData.append("playerId", playerId.toString());
  return api.post(API_ENDPOINTS.PLAYER.PLAYER_SYNC, formData, axiosConfig);
};

export const getPlayerHealth = (
  playerId: number,
  firmGuid: string
): Promise<AxiosResponse<{ health: PlayerHealth }>> => {
  const formData = new FormData();
  formData.append("playerId", playerId.toString());
  formData.append("Firm_id", firmGuid);
  return api.put(API_ENDPOINTS.PLAYER.GET_PLAYER_HEALTH, formData, axiosConfig);
};

export const restartPlayer = (id: string, firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_id", firmGuid);

  return api.post(API_ENDPOINTS.PLAYER.PLAYER_RESTART, formData, axiosConfig);
};

export const getPlayerStats = (
  playerId: number,
  firmGuid: string,
  startDate: string,
  endDate: string
): Promise<AxiosResponse<PlayerStats[]>> => {
  const formData = new FormData();
  formData.append("playerId", playerId.toString());
  formData.append("Firm_id", firmGuid);
  formData.append("StartDate", startDate);
  formData.append("EndDate", endDate);
  return api.put(API_ENDPOINTS.PLAYER.GET_PLAYER_STATS, formData, axiosConfig);
};

export const upsertNotificationToken = (token: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("NotificationToken", token);
  return api.post(API_ENDPOINTS.NOTIFICATION.UPSERT_NOTIFICATION_TOKEN, formData, axiosConfig);
};

export const getNotification = (): Promise<AxiosResponse<AxDataTableResponse<Notification>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 100,
    pageNumber: 1,
  });
  return api.put(API_ENDPOINTS.NOTIFICATION.GET, formData, axiosConfig);
};

export const markAllRead = (): Promise<AxiosResponse<unknown>> => {
  return api.post(API_ENDPOINTS.NOTIFICATION.MARK_ALL_READ, new FormData(), axiosConfig);
};

export const getUnreadNotificationCount = (): Promise<AxiosResponse<number>> => {
  return api.put(API_ENDPOINTS.USER.GET_UNREAD_NOTIFICATION_COUNT, new FormData(), axiosConfig);
};

export const requestNewLicence = (firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("FirmGuid", firmGuid);
  return api.post(API_ENDPOINTS.FIRM_LICENCES.REQUEST_NEW_LICENCE, formData, axiosConfig);
};

export const getSearchResults = (firmGuid: string, query: string): Promise<AxiosResponse<SearchResponseItem[]>> => {
  const formData = new FormData();
  formData.append("FirmGuid", firmGuid);
  formData.append("searchQuery", query);
  return api.put(`${API_ENDPOINTS.FIRM.SEARCH}?FirmGuid=${firmGuid}`, formData, axiosConfig);
};

export const getSystemDynamicSettings = (): Promise<AxiosResponse<AxDataTableResponse<SystemDynamicSetting>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  return api.put(API_ENDPOINTS.SYSTEM_USER_DYNAMIC_SETTING.GET, formData, axiosConfig);
};

export const getUserSystemDynamicSettings = (
  userId: string,
  firmGuid: string
): Promise<AxiosResponse<AxDataTableResponse<SystemDynamicSetting & { systemUserDynamicSettingId: string }>>> => {
  const formData = axTableInput({
    numberOfObjectsPerPage: 999999,
    pageNumber: 1,
  });
  formData.append("UserId", userId.toString());
  formData.append("FirmGuid", firmGuid);
  return api.put(API_ENDPOINTS.SYSTEM_USER_DYNAMIC_SETTING.GET_FOR_USER, formData, axiosConfig);
};

export const addUserSystemDynamicSetting = (
  userId: string,
  id: string,
  firmGuid: string
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("UserId", userId.toString());
  formData.append("SystemUserDynamicSettingId", id.toString());
  formData.append("FirmGuid", firmGuid);
  const url = `${API_ENDPOINTS.SYSTEM_USER_DYNAMIC_SETTING.ADD}/${id}`;

  return api.post(url, formData, axiosConfig);
};

export const removeUserSystemDynamicSetting = (
  userId: string,
  settingUserId: string,
  firmGuid: string
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("UserId", userId.toString());
  formData.append("SystemUserDynamicSettingId", settingUserId.toString());
  formData.append("FirmGuid", firmGuid);
  const url = `${API_ENDPOINTS.SYSTEM_USER_DYNAMIC_SETTING.REMOVE}/${settingUserId}`;

  return api.post(url, formData, axiosConfig);
};

export const changeLicenceExpiry = (
  firmLicenceGuid: string,
  newExpiryDate: string
): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("FirmLicenceGuid", firmLicenceGuid);
  formData.append("NewExpiryDate", newExpiryDate);

  return api.post(API_ENDPOINTS.FIRM.CHANGE_LICENCE_EXPIRY, formData, axiosConfig);
};

export const getUserSubAdmin = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<User>>> => {
  const formData = axTableInput(input);

  if (
    typeof dynamicRequestParams?.sortBy !== "undefined" &&
    typeof dynamicRequestParams?.sortDirection !== "undefined"
  ) {
    formData.append("sortBy", dynamicRequestParams.sortBy.toString());
    formData.append("sortDirection", dynamicRequestParams.sortDirection.toString());
  }

  if (typeof dynamicRequestParams?.firmGuid !== "undefined") {
    formData.append("FirmGuid", dynamicRequestParams.firmGuid.toString());
  }

  return api.put(API_ENDPOINTS.USER_SUB_ADMIN.GET, formData, axiosConfig);
};

export const getUserSubAdminDetails = (userId: string, firmGuid: string): Promise<AxiosResponse<UserDetails>> => {
  const formData = new FormData();
  formData.append("Id", userId.toString());
  formData.append("FirmGuid", firmGuid);

  return api.put(API_ENDPOINTS.USER_SUB_ADMIN.GET_DETAILS, formData, axiosConfig);
};

export const updateUserSubAdmin = (values: {
  id: string;
  name: string;
  surname: string;
  gender: string;
  mobile: string;
  firmGuid: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", values.id.toString());
  formData.append("Name", values.name);
  formData.append("Surname", values.surname);
  formData.append("Gender", values.gender);
  formData.append("Mobile", values.mobile);

  const url = `${API_ENDPOINTS.USER_SUB_ADMIN.UPDATE}/${values.id}?FirmGuid=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const addUserSubAdmin = (values: {
  firmGuid: string;
  name: string;
  surname: string;
  email: string;
  gender: string;
  mobile: string;
}): Promise<AxiosResponse<{ userId: string }>> => {
  const formData = new FormData();
  formData.append("FirmGuid", values.firmGuid);
  formData.append("Name", values.name);
  formData.append("Surname", values.surname);
  formData.append("Email", values.email);
  formData.append("Gender", values.gender);
  formData.append("Mobile", values.mobile);

  return api.post(API_ENDPOINTS.USER_SUB_ADMIN.ADD, formData, axiosConfig);
};

export const deleteUserSubAdmin = (userId: string, firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", userId.toString());
  formData.append("FirmGuid", firmGuid);
  const url = `${API_ENDPOINTS.USER_SUB_ADMIN.DELETE}/${userId}`;

  return api.post(url, formData, axiosConfig);
};

export const getContentGroupMemberCategory = (
  input: AxDataTableRequestInput,
  dynamicRequestParams?: AxDataTableDynamicRequestParams
): Promise<AxiosResponse<AxDataTableResponse<ContentGroupMemberCategory>>> => {
  const formData = axTableInput(input);
  if (typeof dynamicRequestParams?.Firm_Id !== "undefined") {
    formData.append("Firm_Id", dynamicRequestParams.Firm_Id.toString());
  }

  return api.put(API_ENDPOINTS.CONTENT_GROUP_MEMBER_CATEGORY.GET, formData, axiosConfig);
};

export const addContentGroupMemberCategory = (values: {
  name: string;
  firmGuid: string;
}): Promise<AxiosResponse<{ newDataId: string }>> => {
  const formData = new FormData();
  formData.append("Name", values.name);
  formData.append("Firm_Id", values.firmGuid);

  return api.post(API_ENDPOINTS.CONTENT_GROUP_MEMBER_CATEGORY.ADD, formData, axiosConfig);
};

export const deleteContentGroupMemberCategory = (id: string, firmGuid: string): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmGuid);

  const url = `${API_ENDPOINTS.CONTENT_GROUP_MEMBER_CATEGORY.DELETE}?Id=${id}&Firm_Id=${firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const updateContentGroupMemberCategory = (values: {
  id: string;
  name: string;
  firmGuid: string;
}): Promise<AxiosResponse<unknown>> => {
  const formData = new FormData();
  formData.append("Firm_Id", values.firmGuid);
  formData.append("Id", values.id);
  formData.append("Name", values.name);

  const url = `${API_ENDPOINTS.CONTENT_GROUP_MEMBER_CATEGORY.UPDATE}?Id=${values.id}&Firm_Id=${values.firmGuid}`;

  return api.post(url, formData, axiosConfig);
};

export const getContentGroupMemberCategoryDetails = (
  id: string,
  firmGuid: string
): Promise<AxiosResponse<ContentGroupMemberCategory>> => {
  const formData = new FormData();
  formData.append("Id", id);
  formData.append("Firm_Id", firmGuid);
  return api.put(API_ENDPOINTS.CONTENT_GROUP_MEMBER_CATEGORY.GET_DETAILS, formData, axiosConfig);
};
